import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import { GET_COMPOSITE_COMPONENTS_QUERY_KEY } from '@/state/queries/composite-components/use-get-composite-components';
import type {
  CompositeComponentDto,
  CompositeRole,
} from '@/state/queries/composite-components/use-get-composite-components';

export type AllowedInContext = 'event' | 'flow' | 'break' | 'session';

export interface UpdateCompositeComponentDto {
  name?: string;
  slug?: string;
  definition?: {
    version: number;
    components: Array<
      | {
          type: 'html';
          ref: string;
          position: { x: number; y: number; w: number; h: number };
          parameters?: Record<string, string>;
          arrayPath?: string;
        }
      | { type: 'media'; ref: string; position: { x: number; y: number; w: number; h: number } }
    >;
  };
  role?: CompositeRole;
  allowedIn?: AllowedInContext[];
  applicability?: {
    eventTypes?: string[];
    flowScopes?: string[];
    sessionTypes?: string[];
    slotContentTypes?: string[];
  };
  sortOrderByScope?: Record<string, number>;
}

export const useUpdateCompositeComponentMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateCompositeComponentDto }) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/composite-components/${id}`;
      return sendJSONRequest({
        url,
        method: METHOD.PATCH,
        body: data,
        token: token ?? undefined,
      }) as Promise<CompositeComponentDto>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GET_COMPOSITE_COMPONENTS_QUERY_KEY] });
    },
  });
};
